import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox, MetaBoxLayout } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import { fetchInboxItem, updateInboxItem } from '@/features/messages/api';
import { ThreadConversation } from '@/features/messages/ThreadConversation';
import { ApiError } from '@/lib/api/http';

/**
 * 共有受信箱の詳細（事務局専用）。
 * `internal_memo` はこの画面（`GET/PATCH /inbox/:id`）でのみ表示・編集する。
 * 会話本体は `ThreadConversation` を使い回し、そちらには internal_memo を一切渡さない。
 * 状態・優先度・担当者・SLAはUIから外した（実機フィードバック「担当ってなんだ？
 * SLAとかわかりにくい」「複雑な仕様にはしないで」 / D-168）。
 */
export function InboxItemDetailPage(): ReactElement {
  const { inboxItemId = '' } = useParams<{ inboxItemId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['inbox-item', inboxItemId],
    queryFn: () => fetchInboxItem(inboxItemId),
  });

  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.data) setMemo(query.data.internal_memo ?? '');
  }, [query.data]);

  async function applyUpdate(
    payload: Parameters<typeof updateInboxItem>[1],
  ): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateInboxItem(inboxItemId, payload);
      await queryClient.invalidateQueries({ queryKey: ['inbox-item', inboxItemId] });
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  if (query.isPending) return <SkeletonRows rows={4} />;
  if (query.isError) {
    return (
      <ErrorState message="共有受信箱の取得に失敗しました。" onRetry={() => void query.refetch()} />
    );
  }

  const item = query.data;

  return (
    <>
      <PageHeader title={item.requester_name ?? '共有受信箱'} description="受講生などから事務局への問い合わせです。" />
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      <MetaBoxLayout
        main={user && <ThreadConversation threadId={item.thread_id} currentUserId={user.id} />}
        side={
          <div className="flex flex-col gap-3">
            <MetaBox
              title="内部メモ（事務局のみ）"
              footer={
                <button
                  type="button"
                  disabled={busy || memo === (item.internal_memo ?? '')}
                  onClick={() => void applyUpdate({ internal_memo: memo || null })}
                  className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
                >
                  メモを保存
                </button>
              }
            >
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={4}
                placeholder="受講生・理事には表示されません"
                className="w-full rounded-sm border border-wpborder px-3 py-2"
              />
            </MetaBox>
          </div>
        }
      />
    </>
  );
}
